import React from "react";
import {
  FaGithub,
  FaLinkedin,
  FaDownload,
  FaEnvelope,
  FaHtml5,
  FaCss3Alt,
  FaJs,
  FaReact,
  FaPhp,
  FaGit,
  FaDatabase,
  FaGamepad,
  FaUserNinja,
  FaSwimmer,
  FaFilm,
  FaLaptop,
  FaUtensils,
  FaBookOpen,
} from "react-icons/fa";
import { GiSushis } from "react-icons/gi";
import { motion } from "framer-motion";
import profilePic from "../../assets/img/IMG_0180.jpeg";

import {
  SiTailwindcss,
  SiBootstrap,
  SiLaravel,
  SiSymfony,
  SiWordpress,
  SiPhpmyadmin,
} from "react-icons/si";
import { GrDocker, GrMysql } from "react-icons/gr";
import { VscVscode } from "react-icons/vsc";

const SkillIcon = ({ icon: Icon, name, delay }) => (
  <motion.div
    className="flex flex-col items-center justify-center p-2"
    initial={{ opacity: 0, scale: 0 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{
      type: "spring",
      stiffness: 260,
      damping: 20,
      delay: delay,
    }}
  >
    <Icon className="h-10 w-10 mb-1" />
    <span className="text-xs text-gray-300">{name}</span>
  </motion.div>
);

const Home = () => {
  // Animations corrigées avec easings valides
  const heroAnimation = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.6, 0.05, 0.01, 0.9], // Correction de la valeur negative
      },
    },
  };

  const fadeIn = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.6 },
    },
  };

  const fadeInFromRight = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const fadeInFromLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const cardAnimation = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0, 0.71, 0.2, 1.01],
      },
    },
    hover: {
      scale: 1.02,
      boxShadow: "0px 5px 20px rgba(0,0,0,0.15)",
      transition: {
        duration: 0.2,
      },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  return (
    <motion.div
      className="min-h-screen bg-gray-900 text-white pt-8 pb-16 px-4 sm:px-6 lg:px-8"
      initial="hidden"
      animate="visible"
      variants={fadeIn}
    >
      <div className="max-w-7xl mx-auto">
        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Profile Card - Spans 2 columns on larger screens */}
          <motion.div
            className="bg-gray-800 rounded-2xl p-6 shadow-lg md:col-span-2 overflow-hidden relative group"
            variants={heroAnimation}
            whileHover={{ scale: 1.01 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              <motion.div
                className="relative"
                initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                {/* Photo profile with animated border */}
                <div className="w-48 h-48 relative overflow-hidden border rounded-full shadow-md">
                  <motion.img
                    src={profilePic}
                    alt="Clément Rioche"
                    className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-110"
                    whileHover={{ scale: 1.1 }}
                  />
                </div>
              </motion.div>
              <motion.div className="flex-1" variants={fadeInFromRight}>
                <h1 className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">
                  Clément Rioche
                </h1>
                <h2 className="text-xl text-gray-300 mb-4">
                  Développeur Web Full Stack
                </h2>
                <p className="text-gray-400 mb-4 text-justify">
                  Après dix ans d’expérience dans l’optique, dont cinq en tant que responsable, j’ai choisi de me reconvertir dans le développement informatique. Aujourd’hui passionné par la création d’applications modernes et réactives, je souhaite mettre mes compétences au service d’une équipe de développement motivée, dans le cadre d’une alternance, d’un stage ou d’un poste en entreprise. <br />
Je suis ouvert à toute opportunité me permettant de poursuivre ma montée en compétences, contribuer à des projets innovants et m’épanouir dans ce secteur qui me passionne.
                </p>
                <motion.div
                  className="flex space-x-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                >
                  <motion.a
                    href="https://github.com/RiocheClement"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-white transition-colors"
                    whileHover={{ scale: 1.2, rotate: 5 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FaGithub size={24} />
                  </motion.a>
                  <motion.a
                    href="https://linkedin.com/in/clément-rioche-3694921a1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-white transition-colors"
                    whileHover={{ scale: 1.2, rotate: -5 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FaLinkedin size={24} />
                  </motion.a>
                  <motion.a
                    href="mailto:riocheclement@gmail.com"
                    className="text-gray-300 hover:text-white transition-colors"
                    whileHover={{ scale: 1.2, rotate: 5 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FaEnvelope size={24} />
                  </motion.a>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>

          {/* CV Download Card */}
          <motion.div
            className="bg-gray-800 rounded-2xl p-6 shadow-lg flex flex-col justify-between group hover:bg-gray-750 transition-colors duration-300"
            variants={cardAnimation}
            whileHover="hover"
          >
            <h3 className="text-xl font-bold mb-4">
              Mon Curriculum Vitae
            </h3>
            <p className="text-gray-400 mb-6">
              Téléchargez mon CV pour en savoir plus sur mon parcours, mes
              expériences et mes compétences.
            </p>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <motion.a
                href="/assets/files/CV_Clement_Rioche.pdf"
                rel="noopener noreferrer"
                target="_blank"
                download="CV_Clement_Rioche.pdf"
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-300 transform group-hover:scale-105"
                animate={{
                  boxShadow: [
                    "0px 0px 0px rgba(66, 153, 225, 0)",
                    "0px 0px 20px rgba(66, 153, 225, 0.5)",
                    "0px 0px 0px rgba(66, 153, 225, 0)",
                  ],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "mirror",
                }}
              >
                <FaDownload /> Télécharger CV
              </motion.a>
            </motion.div>
          </motion.div>
        </div>

        {/* Skills Bento Grid - 3 cards in a row */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
          variants={staggerContainer}
        >
          {/* Front-end Skills Card */}
          <motion.div
            className="bg-gray-800 rounded-2xl p-6 shadow-lg overflow-hidden group hover:bg-gray-750 transition-colors duration-300"
            variants={cardAnimation}
            whileHover="hover"
          >
            <motion.h3
              className="text-xl font-bold mb-4 flex items-center"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <motion.span
                className="mr-2"
                animate={{
                  rotate: [0, 10, -10, 10, 0],
                }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
              >
                🎨
              </motion.span>
                Front-end Skills
            </motion.h3>
            <div className="grid grid-cols-3 gap-4">
              <SkillIcon icon={FaHtml5} name="HTML5" delay={0.1} />
              <SkillIcon icon={FaCss3Alt} name="CSS3" delay={0.15} />
              <SkillIcon icon={FaJs} name="JavaScript" delay={0.2} />
              <SkillIcon icon={FaReact} name="React" delay={0.25} />
              <SkillIcon icon={SiTailwindcss} name="Tailwind" delay={0.3} />
              <SkillIcon icon={SiWordpress} name="Wordpress" delay={0.35} />
              <SkillIcon icon={SiBootstrap} name="Bootstrap" delay={0.4} />
            </div>
          </motion.div>

          {/* Back-end Skills Card */}
          <motion.div
            className="bg-gray-800 rounded-2xl p-6 shadow-lg overflow-hidden group hover:bg-gray-750 transition-colors duration-300"
            variants={cardAnimation}
            whileHover="hover"
          >
            <motion.h3
              className="text-xl font-bold mb-4 flex items-center"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <motion.span
                className="mr-2"
                animate={{
                  rotate: [0, 10, -10, 10, 0],
                }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
              >
                ⚙️
              </motion.span>
                Back-end Skills
            </motion.h3>
            <div className="grid grid-cols-3 gap-4">
              <SkillIcon icon={FaPhp} name="PHP" delay={0.45} />
              <SkillIcon icon={SiSymfony} name="Symfony" delay={0.5} />
              <SkillIcon icon={SiLaravel} name="Laravel" delay={0.55} />
              <SkillIcon icon={GrMysql} name="MySQL" delay={0.6} />
              <SkillIcon icon={FaDatabase} name="SQL" delay={0.65} />
              <SkillIcon icon={SiPhpmyadmin} name="Phpmyadmin" delay={0.7} />
            </div>
          </motion.div>

          {/* Tools Card */}
          <motion.div
            className="bg-gray-800 rounded-2xl p-6 shadow-lg overflow-hidden group hover:bg-gray-750 transition-colors duration-300"
            variants={cardAnimation}
            whileHover="hover"
          >
            <motion.h3
              className="text-xl font-bold mb-4 flex items-center"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <motion.span
                className="mr-2"
                animate={{
                  rotate: [0, 10, -10, 10, 0],
                }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
              >
                🛠️
              </motion.span>
                Outils & Environnements
            </motion.h3>
            <div className="grid grid-cols-3 gap-4">
              <SkillIcon icon={FaGit} name="Git" delay={0.75} />
              <SkillIcon icon={VscVscode} name="VS Code" delay={0.8} />
              <SkillIcon icon={GrDocker} name="Docker" delay={0.85} />
            </div>
          </motion.div>
        </motion.div>

        {/* Quote/About Card - Full width */}
        <motion.div
          className="bg-gray-800 rounded-2xl p-8 shadow-lg mb-8 relative overflow-hidden group"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          whileHover={{ scale: 1.01 }}
        >
          <motion.div
            className="absolute -top-20 -right-20 w-64 h-64 bg-blue-500 opacity-20 rounded-full blur-3xl group-hover:opacity-30 transition-opacity duration-500"
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.2, 0.3, 0.2],
            }}
            transition={{ duration: 8, repeat: Infinity }}
          ></motion.div>
          <div className="relative">
            <motion.h3
              className="text-2xl font-bold mb-4"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">
                Ma Vision
              </span>
            </motion.h3>
            <motion.p
              className="text-gray-300 italic mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              "Je crois fermement que la technologie doit être au service des
              utilisateurs. Mon expérience en relation client m'a appris
              l'importance de créer des solutions qui répondent aux besoins
              réels, avec une attention particulière portée à l'expérience
              utilisateur."
            </motion.p>
            <motion.p
              className="text-gray-400"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              Ma rigueur, mon autonomie et ma capacité d'analyse, développées
              lors de ma précédente expérience professionnelle, me permettent de
              m'adapter rapidement à de nouveaux environnements et d'apporter
              une réelle valeur ajoutée à chaque projet.
            </motion.p>
          </div>
        </motion.div>

        {/* Current Focus - 2 cards side by side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <motion.div
            className="bg-gray-800 rounded-2xl p-6 shadow-lg group hover:bg-gray-750 transition-colors duration-300"
            variants={fadeInFromLeft}
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className="text-xl font-bold mb-4 flex items-center">
              <motion.span
                className="mr-2"
                animate={{
                  scale: [1, 1.2, 1],
                }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
              >
                🎯
              </motion.span>
                Objectifs Professionnels
            </h3>
            <motion.ul
              className="text-gray-300 space-y-2"
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              {[
                "Mettre mes compétences au service d'une équipe de développement",
                "Contribuer à des projets innovants et stimulants",
                "Développer mes compétences actuelles",
                "Développer de nouvelles compétences dans d'autres languages de programmation",
                "Participer à des projets de transformation digitale",
              ].map((item, index) => (
                <motion.li
                  key={index}
                  className="flex items-start"
                  variants={{
                    hidden: { opacity: 0, x: -10 },
                    visible: {
                      opacity: 1,
                      x: 0,
                      transition: { delay: 0.5 + index * 0.1 },
                    },
                  }}
                >
                  <span className="text-blue-400 mr-2">•</span>
                  {item}
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          <motion.div
            className="bg-gray-800 rounded-2xl p-6 shadow-lg group hover:bg-gray-750 transition-colors duration-300"
            variants={fadeInFromRight}
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className="text-xl font-bold mb-4 flex items-center">
              <motion.span
                className="mr-2"
                animate={{
                  rotate: [0, 10, -10, 10, 0],
                }}
                transition={{ duration: 3, repeat: Infinity, repeatDelay: 1 }}
              >
                💡
              </motion.span>
                Soft Skills
            </h3>
            <motion.div
              className="grid grid-cols-2 gap-3"
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              {[
                { name: "Résolution de problèmes", value: "90%" },
                { name: "Communication", value: "85%" },
                { name: "Autonomie", value: "95%" },
                { name: "Travail d'équipe", value: "100%" },
              ].map((skill, index) => (
                <motion.div
                  key={index}
                  className="bg-gray-700 rounded-lg p-3"
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: { delay: 0.5 + index * 0.1 },
                    },
                  }}
                >
                  <h4 className="font-medium mb-1">{skill.name}</h4>
                  <div className="w-full bg-gray-600 rounded-full h-2">
                    <motion.div
                      className="bg-blue-500 h-2 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: skill.value }}
                      transition={{ duration: 1, delay: 0.7 + index * 0.1 }}
                    ></motion.div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* Passions/Hobbies Card - Full width */}
        <motion.div
          className="bg-gray-800 rounded-2xl p-6 shadow-lg mb-8 relative overflow-hidden"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.7 }}
          whileHover={{ scale: 1.01 }}
        >
          <motion.div
            className="absolute -bottom-20 -left-20 w-64 h-64 bg-purple-500 opacity-10 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.2, 0.1],
            }}
            transition={{ duration: 6, repeat: Infinity }}
          ></motion.div>

          <div className="relative">
            <motion.h3
              className="text-xl font-bold mb-6 flex items-center"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">
                Passions & Intérêts
              </span>
            </motion.h3>

            {/* Content grid */}
            <motion.div
              className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              {/* Catégorie Sports */}
              <div className="bg-gray-700 bg-opacity-50 p-4 rounded-xl">
                <h4 className="text-blue-400 font-medium mb-3 flex items-center">
                <motion.span
                    className="mr-2 text-xl"
                    animate={{ rotate: [0, 5, 0, -5, 0] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "loop"
                    }}
                  >
                    🥋
                  </motion.span>
                  Sports
                </h4>
                <div className="grid grid-cols-1 gap-3">
                  <motion.div
                    className="flex items-center"
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2, type: "tween" }}
                  >
                    <motion.div
                      className="mr-3 text-blue-400"
                      whileHover={{ scale: 1.2, rotate: 360 }}
                      transition={{ duration: 0.5 }}
                      initial={false}
                    >
                      <FaUserNinja size={18} />
                    </motion.div>
                    <span className="text-sm text-gray-300">Arts Martiaux</span>
                  </motion.div>

                  <motion.div
                    className="flex items-center"
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2, type: "tween" }}
                  >
                    <motion.div
                      className="mr-3 text-blue-400"
                      whileHover={{ scale: 1.2, rotate: 10 }}
                      transition={{ duration: 0.3 }}
                      initial={false}
                    >
                      <FaSwimmer size={18} />
                    </motion.div>
                    <span className="text-sm text-gray-300">Natation</span>
                  </motion.div>
                </div>
              </div>

              {/* Catégorie Multimédia */}
              <div className="bg-gray-700 bg-opacity-50 p-4 rounded-xl">
                <h4 className="text-purple-400 font-medium mb-3 flex items-center">
                  <motion.span
                    className="mr-2 text-xl"
                    animate={{ rotate: [0, 5, 0, -5, 0] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "loop"
                    }}
                  >
                    🎮
                  </motion.span>
                  Multimédia
                </h4>
                <div className="grid grid-cols-1 gap-3">
                  <motion.div
                    className="flex items-center"
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2, type: "tween" }}
                  >
                    <motion.div
                      className="mr-3 text-purple-400"
                      whileHover={{ scale: 1.2, rotate: 360 }}
                      transition={{ duration: 0.5 }}
                      initial={false}
                    >
                      <FaGamepad size={18} />
                    </motion.div>
                    <span className="text-sm text-gray-300">Jeux Vidéo</span>
                  </motion.div>

                  <motion.div
                    className="flex items-center"
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2, type: "tween" }}
                  >
                    <motion.div
                      className="mr-3 text-purple-400"
                      whileHover={{ scale: 1.2, rotate: 10 }}
                      transition={{ duration: 0.3 }}
                      initial={false}
                    >
                      <FaLaptop size={18} />
                    </motion.div>
                    <span className="text-sm text-gray-300">Informatique</span>
                  </motion.div>

                  <motion.div
                    className="flex items-center"
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2, type: "tween" }}
                  >
                    <motion.div
                      className="mr-3 text-purple-400"
                      whileHover={{ scale: 1.2, rotate: 10 }}
                      transition={{ duration: 0.3 }}
                      initial={false}
                    >
                      <FaFilm size={18} />
                    </motion.div>
                    <span className="text-sm text-gray-300">Cinéma</span>
                  </motion.div>
                </div>
              </div>

              {/* Catégorie Culture & Cuisine */}
              <div className="bg-gray-700 bg-opacity-50 p-4 rounded-xl">
                <h4 className="text-red-400 font-medium mb-3 flex items-center">
                <motion.span
                    className="mr-2 text-xl"
                    animate={{ rotate: [0, 5, 0, -5, 0] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "loop"
                    }}
                  >
                    🍜
                  </motion.span>
                  Culture & Cuisine
                </h4>
                <div className="grid grid-cols-1 gap-3">
                  <motion.div
                    className="flex items-center"
                    whileHover={{
                      x: 5,
                      transition: { duration: 0.2, type: "tween" },
                    }}
                  >
                    <motion.div
                      className="mr-3 text-red-400"
                      whileHover={{ scale: 1.2, rotate: 360 }}
                      transition={{ duration: 0.5 }}
                      initial={false}
                    >
                      日
                    </motion.div>
                    <span className="text-sm text-gray-300">
                      Culture Japonaise
                    </span>
                  </motion.div>

                  <motion.div
                    className="flex items-center"
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2, type: "tween" }}
                  >
                    <motion.div
                      className="mr-3 text-red-400"
                      whileHover={{ scale: 1.2, rotate: 10 }}
                      transition={{ duration: 0.3 }}
                      initial={false}
                    >
                      <FaBookOpen size={18} />
                    </motion.div>
                    <span className="text-sm text-gray-300">
                      Mangas & Anime
                    </span>
                  </motion.div>

                  <motion.div
                    className="flex items-center"
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2, type: "tween" }}
                  >
                    <motion.div
                      className="mr-3 text-red-400"
                      whileHover={{ scale: 1.2, rotate: 10 }}
                      transition={{ duration: 0.3 }}
                      initial={false}
                    >
                      <GiSushis size={18} />
                    </motion.div>
                    <span className="text-sm text-gray-300">
                      Cuisine Japonaise
                    </span>
                  </motion.div>

                  <motion.div
                    className="flex items-center"
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2, type: "tween" }}
                  >
                    <motion.div
                      className="mr-3 text-red-400"
                      whileHover={{ scale: 1.2, rotate: 10 }}
                      transition={{ duration: 0.3 }}
                      initial={false}
                    >
                      <FaUtensils size={18} />
                    </motion.div>
                    <span className="text-sm text-gray-300">
                      Cuisine en général
                    </span>
                  </motion.div>
                </div>
              </div>
            </motion.div>

            <motion.p
              className="text-gray-400 mt-4 italic text-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              "Je crois fermement que maintenir un équilibre entre vie
              professionnelle et personnelle stimule la créativité et favorise
              une approche plus humaine dans mon travail."
            </motion.p>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Home;
